import React from 'react';
import { AlertTriangle, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой оценки риска банкротства
 */
const QuickAssessmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  companyTypes,
  industryTypes,
  bankruptcyModels
}) => {
  // Расчет текущих значений
  const totalAssets = parseFloat(formData.totalAssets) || 0;
  const totalLiabilities = parseFloat(formData.totalLiabilities) || 0;
  const equity = totalAssets - totalLiabilities;
  const debtRatio = totalLiabilities / totalAssets || 0;
  
  // Обработчик изменения суммы активов
  const handleTotalAssetsChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      totalAssets: newValue,
      // Автоматически обновляем рабочий капитал
      workingCapital: (parseFloat(prev.currentAssets) || 0) - (parseFloat(prev.currentLiabilities) || 0)
    }));
  };
  
  // Обработчик изменения суммы обязательств
  const handleTotalLiabilitiesChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      totalLiabilities: newValue,
      // Автоматически обновляем рабочий капитал
      workingCapital: (parseFloat(prev.currentAssets) || 0) - (parseFloat(prev.currentLiabilities) || 0)
    }));
  };
  
  // Обработчик изменения типа компании
  const handleCompanyTypeChange = (companyType) => {
    setFormData(prev => ({ ...prev, companyType }));
  };
  
  // Обработчик изменения отрасли
  const handleIndustryTypeChange = (industryType) => {
    setFormData(prev => ({ ...prev, industryType }));
  };
  
  // Обработчик изменения модели расчета
  const handleCalculationModelChange = (calculationModel) => {
    setFormData(prev => ({ ...prev, calculationModel }));
  };
  
  // Функция запуска расчета
  const runCalculation = () => {
    // Отправляем форму для расчета
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные шаблоны компаний
  const companyTemplates = [
    { 
      name: 'Малый бизнес',
      totalAssets: '1000000',
      totalLiabilities: '750000',
      currentAssets: '400000',
      currentLiabilities: '300000',
      companyType: 'individual',
      industryType: 'retail'
    },
    { 
      name: 'Средний бизнес',
      totalAssets: '10000000',
      totalLiabilities: '6000000',
      currentAssets: '4000000',
      currentLiabilities: '2500000',
      companyType: 'llc',
      industryType: 'services'
    },
    { 
      name: 'Производство',
      totalAssets: '50000000',
      totalLiabilities: '30000000',
      currentAssets: '15000000',
      currentLiabilities: '10000000',
      companyType: 'jsc',
      industryType: 'manufacturing'
    },
    { 
      name: 'Стартап',
      totalAssets: '3000000',
      totalLiabilities: '2500000',
      currentAssets: '1000000',
      currentLiabilities: '900000',
      companyType: 'llc',
      industryType: 'it'
    }
  ];
  
  // Применение шаблона компании
  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      totalAssets: template.totalAssets,
      totalLiabilities: template.totalLiabilities,
      currentAssets: template.currentAssets,
      currentLiabilities: template.currentLiabilities,
      companyType: template.companyType,
      industryType: template.industryType,
      workingCapital: (parseFloat(template.currentAssets) || 0) - (parseFloat(template.currentLiabilities) || 0)
    }));
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-orange-500" />
        Быстрая оценка
      </h3>
      
      <div className="space-y-5">
        {/* Основные финансовые показатели */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="totalAssetsSlider" className="text-gray-600 dark:text-gray-400">Общие активы</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(totalAssets, 0)}₽</span>
          </div>
          <input
            type="range"
            id="totalAssetsSlider"
            min="100000"
            max="100000000"
            step="100000"
            value={totalAssets}
            onChange={handleTotalAssetsChange}
            aria-label="Общие активы"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-600"
          />
          
          <div className="flex justify-between items-center mt-3 mb-1 text-sm">
            <label htmlFor="totalLiabilitiesSlider" className="text-gray-600 dark:text-gray-400">Общие обязательства</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(totalLiabilities, 0)}₽</span>
          </div>
          <input
            type="range"
            id="totalLiabilitiesSlider"
            min="0"
            max={totalAssets}
            step="100000"
            value={totalLiabilities}
            onChange={handleTotalLiabilitiesChange}
            aria-label="Общие обязательства"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-600"
          />
          
          {/* Рассчитанные показатели */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mt-3 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Собственный капитал</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{formatNumber(equity, 0)}₽</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Коэффициент долга</span>
              <span className={`text-sm font-medium ${
                debtRatio <= 0.5 ? 'text-green-600 dark:text-green-400' :
                debtRatio <= 0.7 ? 'text-yellow-600 dark:text-yellow-400' :
                'text-red-600 dark:text-red-400'
              }`}>
                {formatNumber(debtRatio, 2)} ({formatNumber(debtRatio * 100, 0)}%)
              </span>
            </div>
          </div>
        </div>
        
        {/* Модель оценки */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">Модель оценки</div>
          <div className="grid grid-cols-1 gap-2">
            {bankruptcyModels.slice(0, 4).map(model => (
              <button
                key={model.id}
                type="button"
                onClick={() => handleCalculationModelChange(model.id)}
                className={`p-2 text-xs rounded-lg border transition-colors text-left
                  ${formData.calculationModel === model.id 
                    ? 'bg-orange-100 border-orange-300 text-orange-700 dark:bg-orange-900/30 dark:border-orange-700 dark:text-orange-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {model.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Шаблоны компаний */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">Шаблоны для быстрого расчета</div>
          <div className="grid grid-cols-2 gap-2">
            {companyTemplates.map((template, index) => (
              <button
                key={index}
                type="button"
                onClick={() => applyTemplate(template)}
                className="p-2 text-xs rounded-lg border bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={runCalculation}
          icon={<AlertTriangle className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Оценить риск банкротства
        </Button>
      </div>
    </div>
  );
};

export default QuickAssessmentSidebar;